// paex-attach.js — Finish the look (<paex-addon>) + Complete the costume
// (<paex-complete>) — Store Availability UX v2, design 2a-2f. Ascent
// conventions: webvista pub/sub, AbortController teardown, light DOM.
// Re-execution-safe (both blocks emit the tag; cache serves one file).
//
// Cart writes mirror the native product-form contract exactly: every
// /cart/add.js + /cart/change.js POST carries `sections` + `sections_url`,
// the response HTML is applied through SectionDynamicUpdate (header bubble +
// drawer — the ONLY way Ascent re-renders cart UI), and only then is
// PUB_SUB_EVENTS.cartUpdate published. The native Add-to-cart flow is NEVER
// intercepted — the add-on absorption runs AFTER the native add succeeds
// (two-step; nothing native is edited). keepalive covers the page-cart mode
// where product-form navigates to /cart right after publishing.

(function () {
  'use strict';

  // M5 (2026-08-03): the shared cart-bridge primitives (money ladder,
  // sections descriptors, retrap, publish + the W3 typeof-gate lore) live in
  // paex-cart-bridge.js — emitted right before this file by every consumer's
  // Liquid, deferred, so it is defined first. Looked up at call time; when
  // absent each helper degrades exactly like the old typeof-gates did.
  function bridge() {
    return window.PaexCartBridge || null;
  }

  function routeRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function money(cents) {
    const b = bridge();
    return b ? b.money(cents) : `$${(cents / 100).toFixed(2)}`;
  }

  function publishCart(cart) {
    const b = bridge();
    if (b) b.publishCart('paex-attach', cart);
  }

  // same descriptor shapes as product-form.getSectionsToRender();
  // silence-gated drawer stays this consumer's policy
  function sectionsToRender() {
    let drawer = document.getElementById('Cart-Drawer');
    if (drawer && drawer.hasAttribute('data-status-silence')) drawer = null;
    const b = bridge();
    if (b) return b.sectionsToRender(drawer);
    return [{ id: 'Cart-Icon-Bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
  }

  function patchSections(response) {
    if (!response || !response.sections) return;
    if (typeof SectionDynamicUpdate === 'undefined') return;
    SectionDynamicUpdate.updateSections(sectionsToRender(), response.sections);
    retrapDrawer();
  }

  function retrapDrawer() {
    const b = bridge();
    if (b) b.retrapDrawer(null);
  }

  // one mutation = one section patch + one publish (product-form ordering)
  function cartMutate(path, payload) {
    // M6 (2026-08-27): prefer the bridge queue — the absorb() add can no
    // longer race the parent add's paint, and a stale response can never
    // overwrite a newer one (token-gated). keepalive rides inside the bridge.
    const b = bridge();
    if (b && typeof b.mutate === 'function') {
      return b.mutate(path, payload, { source: 'paex-attach', keepalive: true })
        .then((response) => (response && !response.status ? response : null))
        .catch(() => null);
    }
    const body = Object.assign({}, payload, {
      sections: sectionsToRender().map((section) => section.section).join(),
      sections_url: window.location.pathname,
    });
    return fetch(`${routeRoot()}${path}`, {
      method: 'POST',
      cache: 'no-store',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((response) => {
        if (!response) return null;
        patchSections(response);
        publishCart(response);
        return response;
      })
      .catch(() => null);
  }

  function addItems(items) {
    return cartMutate('cart/add.js', { items });
  }

  // id: prefer the LINE KEY — carts can hold several lines of one variant
  // (properties, automatic discounts), and change.js by variant id would pick
  // its own first match
  function changeQuantity(id, quantity) {
    return cartMutate('cart/change.js', { id, quantity });
  }

  function fetchCart() {
    return fetch(`${routeRoot()}cart.js`, { cache: 'no-store' }) // M6: never resolve a key from a cached cart
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }

  // undo removes ONE unit — never wipes a pre-existing line of the same
  // accessory. A line that already vanished (size switched, drawer removal)
  // counts as done.
  function removeOneOf(variantId) {
    return fetchCart().then((cart) => {
      if (!cart) return { ok: false, remaining: 1 }; // fetch failed — not a success
      const line = cart.items && cart.items.find((item) => item.variant_id === Number(variantId));
      if (!line) return { ok: true, remaining: 0 };
      const remaining = Math.max(0, line.quantity - 1);
      return changeQuantity(line.key, remaining).then((response) => ({ ok: !!response, remaining }));
    });
  }

  function pushEvent(enabled, name, payload) {
    if (!enabled) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, payload));
  }

  /* ---------------- BOPIS (2026-10-22, lane E, Track 3 step 9) ----------------
   * The Liquid renders both blocks already filtered for the cart's mode (the
   * attribute paex_fulfil at page render). When the mode moves on the page
   * (paex:fulfil-change: a first add set it, Change switched it, an empty cart
   * cleared it) the lists re-check themselves from the data-fulfil-stock stamp
   * each toggle / row button / size option carries ({dc, stores:{loc: qty}}):
   * ship = the DC entry (absent = unknown = shown; 0 = hidden), pickup /
   * same-day = the store entry (> 0). Hidden rows get data-fulfil-hidden
   * (applyCollapse honours it); an emptied block hides its wrapper (no empty
   * frame). No mode = everything shown = today's lists. Rows the shopper already
   * added stay (receipts). Feature-detects window.paexFulfil: absent, no-op.
   */
  function fulfilMode() {
    const api = window.paexFulfil;
    if (!api || typeof api.mode !== 'function') return null;
    try {
      const mode = api.mode();
      if (!mode) return null;
      const service = api.service(mode);
      const loc = service === 'ship' ? api.onlineLocId() : api.locationId(mode);
      return service ? { service, loc: loc == null ? '' : String(loc) } : null;
    } catch (error) {
      return null;
    }
  }

  function fulfilStockOf(element) {
    if (!element || !element.getAttribute) return null;
    const raw = element.getAttribute('data-fulfil-stock');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  // true when the stamped variant exists for the cart's service (unknown = true)
  function fulfilOk(element, mode) {
    if (!mode) return true;
    const stock = fulfilStockOf(element);
    if (!stock) return true;
    if (mode.service === 'ship') return !(stock.dc != null && Number(stock.dc) <= 0);
    const qty = stock.stores && stock.stores[mode.loc];
    return qty != null && Number(qty) > 0;
  }

  function fulfilWrapper(element) {
    return element.closest('.product-info-block') || element;
  }

  /* ---------------- Finish the look ---------------- */

  if (!customElements.get('paex-addon')) {
    customElements.define(
      'paex-addon',
      class PaexAddon extends HTMLElement {
        connectedCallback() {
          if (this.dataset.originalSection) return; // quick-view clone — native guard pattern
          this.abortController = new AbortController();
          const signal = this.abortController.signal;
          this.analytics = this.dataset.analytics === 'true';
          this.parentVariants = (this.dataset.parentVariants || '').split(',');
          this.busy = false;
          this.originalCta = null;
          this.appliedLabel = null;

          this.querySelectorAll('[data-addon-toggle]').forEach((toggle) => {
            toggle.addEventListener('click', () => {
              const on = toggle.getAttribute('aria-checked') === 'true';
              toggle.setAttribute('aria-checked', on ? 'false' : 'true');
              const card = toggle.closest('[data-addon-card]');
              if (card) card.classList.toggle('is-on', !on);
              const eyebrow = card && card.querySelector('[data-addon-eyebrow]');
              if (eyebrow) {
                eyebrow.textContent = !on ? eyebrow.dataset.labelOn : eyebrow.dataset.labelOff;
              }
              this.syncCta();
              pushEvent(this.analytics, 'addon_toggled', {
                parent_sku: this.dataset.parentSku || '',
                addon_sku: toggle.dataset.sku || '',
                on: !on,
              });
            }, { signal });
          });

          // absorb the toggled add-ons right after the NATIVE add succeeds.
          // webvista.publish has no per-callback try/catch — stay throw-safe.
          this.cartUnsubscriber = typeof webvista !== 'undefined' && webvista.subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            try {
              this.absorb(event);
            } catch (error) {
              this.busy = false;
            }
          });

          // native variant change rewrites the submit label from fresh HTML —
          // re-snapshot and re-apply after it lands
          this.variantUnsubscriber = typeof webvista !== 'undefined' && webvista.subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            try {
              if (!event || !event.data || event.data.sectionId !== this.dataset.section) return;
              requestAnimationFrame(() => this.resyncCta());
            } catch (error) {
              /* no-op */
            }
          });

          // BOPIS: the cart mode moved → re-check every add-on for the new service
          document.addEventListener('paex:fulfil-change', () => {
            try { this.applyFulfil(); } catch (error) { /* additive */ }
          }, { signal });
          this.applyFulfil();
        }

        disconnectedCallback() {
          if (this.abortController) this.abortController.abort();
          if (this.cartUnsubscriber) this.cartUnsubscriber();
          if (this.variantUnsubscriber) this.variantUnsubscriber();
          this.restoreCta();
        }

        // BOPIS: hide the cards the cart's service cannot fulfil (toggle off first), the whole
        // block when none remains; no mode = every card shown (today)
        applyFulfil() {
          const mode = fulfilMode();
          let shown = 0;
          this.querySelectorAll('[data-addon-card]').forEach((card) => {
            const toggle = card.querySelector('[data-addon-toggle]');
            const ok = fulfilOk(toggle, mode);
            if (!ok && toggle && toggle.getAttribute('aria-checked') === 'true') {
              toggle.setAttribute('aria-checked', 'false');
              card.classList.remove('is-on');
              const eyebrow = card.querySelector('[data-addon-eyebrow]');
              if (eyebrow) eyebrow.textContent = eyebrow.dataset.labelOff;
            }
            card.toggleAttribute('data-fulfil-hidden', !ok);
            card.hidden = !ok;
            if (ok) shown += 1;
          });
          fulfilWrapper(this).hidden = shown === 0;
          this.syncCta();
        }

        // only the parent product's own add may trigger absorption — quick
        // view forms publish the same 'product-form' source for other products
        absorb(event) {
          if (!event || event.source !== 'product-form') return;
          const added = event.productVariantId ? String(event.productVariantId) : '';
          if (!added || this.parentVariants.indexOf(added) === -1) return;
          const toggled = this.toggledVariants();
          if (!toggled.length || this.busy) return;
          this.busy = true;
          addItems(toggled.map((variantId) => ({ id: Number(variantId), quantity: 1 })))
            .then((response) => {
              this.busy = false;
              if (response) {
                this.reset();
                this.announce(this.dataset.labelAdded || '');
              } else {
                // add-on add failed — keep the toggles ON so the state is
                // honest, and tell AT users what happened
                this.announce(this.dataset.labelError || '');
              }
            });
        }

        announce(message) {
          const live = this.querySelector('[data-attach-live]');
          if (!live || !message) return;
          live.textContent = message;
          setTimeout(() => {
            if (live.textContent === message) live.textContent = '';
          }, 4000);
        }

        toggledVariants() {
          return Array.from(this.querySelectorAll('[data-addon-toggle][aria-checked="true"]'))
            .map((toggle) => toggle.dataset.variantId)
            .filter(Boolean);
        }

        nativeCta() {
          return document.getElementById(`Product-Submit-Button-${this.dataset.section}`);
        }

        ctaLabelEl() {
          const cta = this.nativeCta();
          if (!cta) return null;
          return cta.querySelector('.button-text') || cta.querySelector('span') || cta;
        }

        // the CTA absorbs the add-on (design 2b) — label only, never a price.
        // A disabled button (Sold out / preorder gating) is native territory.
        syncCta() {
          const cta = this.nativeCta();
          const label = this.ctaLabelEl();
          // BOPIS (2026-10-22, seams §1 step 2): a pickup / same-day label is owned by the availability block
          if (!cta || !label || cta.disabled || cta.dataset.fulfilLabel) return;
          const count = this.toggledVariants().length;
          if (!this.originalCta) this.originalCta = label.textContent;
          if (count === 0) {
            label.textContent = this.originalCta;
            this.appliedLabel = null;
          } else if (count === 1) {
            label.textContent = this.dataset.labelOne || this.originalCta;
            this.appliedLabel = label.textContent;
          } else {
            label.textContent = (this.dataset.labelMany || '').replace('{n}', String(count + 1)) || this.originalCta;
            this.appliedLabel = label.textContent;
          }
        }

        // after a native re-render the current text is native-owned — cache it
        // as the new original; if our applied label survived, nothing moved
        resyncCta() {
          const label = this.ctaLabelEl();
          if (!label) return;
          if (this.appliedLabel && label.textContent === this.appliedLabel) return;
          this.originalCta = null;
          this.appliedLabel = null;
          this.syncCta();
        }

        restoreCta() {
          const cta = this.nativeCta();
          const label = this.ctaLabelEl();
          if (!cta || !label || cta.disabled) return;
          if (this.originalCta && this.appliedLabel && label.textContent === this.appliedLabel) {
            label.textContent = this.originalCta;
          }
        }

        reset() {
          this.querySelectorAll('[data-addon-toggle][aria-checked="true"]').forEach((toggle) => {
            toggle.setAttribute('aria-checked', 'false');
            const card = toggle.closest('[data-addon-card]');
            if (card) card.classList.remove('is-on');
            const eyebrow = card && card.querySelector('[data-addon-eyebrow]');
            if (eyebrow) eyebrow.textContent = eyebrow.dataset.labelOff;
          });
          this.syncCta();
        }
      }
    );
  }

  /* ---------------- Complete the costume ---------------- */

  if (!customElements.get('paex-complete')) {
    customElements.define(
      'paex-complete',
      class PaexComplete extends HTMLElement {
        connectedCallback() {
          if (this.dataset.originalSection) return; // quick-view clone — native guard pattern
          this.abortController = new AbortController();
          const signal = this.abortController.signal;
          this.analytics = this.dataset.analytics === 'true';
          this.expanded = false;

          this.rowsHost = this.querySelector('[data-complete-rows]');
          this.seeAlls = Array.from(this.querySelectorAll('[data-see-all]')); // pill (mobile) + head link (desktop)
          // breakpoint source must exist before the first applyCollapse
          this.mql = window.matchMedia('(min-width: 750px)');

          // added rows sort to the top (design 2d) — includes SSR'd ones
          this.querySelectorAll('[data-complete-row].is-added').forEach((row) => {
            this.rowsHost.prepend(row);
          });
          this.applyCollapse();
          this.syncTotal();
          // hands visibility over from the SSR pre-collapse <style> to the
          // hidden attributes applyCollapse just set — same rows, no shift
          this.setAttribute('data-ready', '');
          // live region armed AFTER hydration: an SSR'd in-cart total must
          // not announce on page load
          const total = this.querySelector('[data-complete-total]');
          if (total) {
            setTimeout(() => {
              total.setAttribute('aria-live', 'polite');
              total.setAttribute('aria-atomic', 'true');
            }, 0);
          }

          this.seeAlls.forEach((control) => {
            control.addEventListener('click', () => {
              this.expanded = !this.expanded;
              this.applyCollapse();
            }, { signal });
          });

          this.querySelectorAll('[data-row-variant]').forEach((select) => {
            select.addEventListener('change', () => {
              const row = select.closest('[data-complete-row]');
              const add = row && row.querySelector('[data-row-add]');
              const option = select.options[select.selectedIndex];
              if (add && option) {
                add.dataset.variantId = option.value;
                add.dataset.price = option.dataset.price || add.dataset.price;
                if (option.dataset.fulfilStock) add.dataset.fulfilStock = option.dataset.fulfilStock; // BOPIS: the row's stamp follows the size
                const price = row.querySelector('[data-row-price]');
                if (price && option.dataset.price) price.textContent = money(Number(option.dataset.price));
              }
            }, { signal });
          });
          // an Added row is a receipt: its size is locked until undone —
          // switching it would desync the row state from the cart line
          this.syncRowLocks();

          this.querySelectorAll('[data-row-add]').forEach((button) => {
            button.addEventListener('click', () => this.toggleRow(button), { signal });
          });

          // window resizes across the breakpoint change the collapsed count
          this.onMql = () => this.applyCollapse();
          if (this.mql.addEventListener) this.mql.addEventListener('change', this.onMql);

          // the running total sums FROM the parent price — keep it current
          // across native variant switches
          this.variantUnsubscriber = typeof webvista !== 'undefined' && webvista.subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            try {
              if (!event || !event.data || event.data.sectionId !== this.dataset.section) return;
              const variant = event.data.variant;
              if (!variant) return;
              if (variant.price != null) this.dataset.parentPrice = String(variant.price);
              if (variant.sku != null) this.dataset.parentSku = variant.sku;
              this.syncTotal();
            } catch (error) {
              /* no-op */
            }
          });

          // BOPIS: the cart mode moved → re-check every row (and its sizes) for the new service
          document.addEventListener('paex:fulfil-change', () => {
            try { this.applyFulfil(); } catch (error) { /* additive */ }
          }, { signal });
          this.applyFulfil();
        }

        // BOPIS: a row stays when its variant (or any listed size) exists for the cart's service;
        // sizes the service cannot fulfil leave the select and the first good one is picked;
        // added rows are receipts and never hide; an emptied list hides the whole block
        applyFulfil() {
          const mode = fulfilMode();
          let shown = 0;
          this.querySelectorAll('[data-complete-row]').forEach((row) => {
            const add = row.querySelector('[data-row-add]');
            const select = row.querySelector('[data-row-variant]');
            let ok = true;
            if (row.classList.contains('is-added')) {
              ok = true;
            } else if (select) {
              let firstOk = null;
              Array.from(select.options).forEach((option) => {
                const good = fulfilOk(option, mode);
                option.hidden = !good;
                option.disabled = !good;
                if (good && !firstOk) firstOk = option;
              });
              const current = select.options[select.selectedIndex];
              if (current && (current.hidden || current.disabled) && firstOk) {
                select.value = firstOk.value;
                select.dispatchEvent(new Event('change', { bubbles: true }));
              }
              ok = !!firstOk;
            } else if (add) {
              ok = fulfilOk(add, mode);
            }
            row.toggleAttribute('data-fulfil-hidden', !ok);
            if (ok) shown += 1;
          });
          fulfilWrapper(this).hidden = shown === 0;
          this.applyCollapse();
          this.syncTotal();
        }

        disconnectedCallback() {
          if (this.abortController) this.abortController.abort();
          if (this.variantUnsubscriber) this.variantUnsubscriber();
          if (this.mql && this.mql.removeEventListener) this.mql.removeEventListener('change', this.onMql);
        }

        announce(message) {
          const live = this.querySelector('[data-attach-live]');
          if (!live || !message) return;
          live.textContent = message;
          setTimeout(() => {
            if (live.textContent === message) live.textContent = '';
          }, 4000);
        }

        syncRowLocks() {
          this.querySelectorAll('[data-complete-row]').forEach((row) => {
            const select = row.querySelector('[data-row-variant]');
            if (select) select.disabled = row.classList.contains('is-added');
          });
        }

        rowsLimit() {
          const desktop = this.mql && this.mql.matches;
          return Number(desktop ? this.dataset.rowsDesktop : this.dataset.rowsMobile) || 3;
        }

        // "See all" expands in place, never navigates (design 2d)
        applyCollapse() {
          // BOPIS: rows out for the cart's service are hidden regardless and never count
          const rows = Array.from(this.querySelectorAll('[data-complete-row]')).filter((row) => {
            const out = row.hasAttribute('data-fulfil-hidden');
            if (out) row.hidden = true;
            return !out;
          });
          const limit = this.rowsLimit();
          rows.forEach((row, index) => {
            // "collapse never hides an item they picked" (design 2d)
            row.hidden = !this.expanded && index >= limit && !row.classList.contains('is-added');
          });
          this.seeAlls.forEach((control) => {
            control.hidden = rows.length <= limit;
            control.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
            control.textContent = this.expanded
              ? this.dataset.labelShowFewer
              : this.dataset.labelSeeAll;
          });
        }

        toggleRow(button) {
          if (button.disabled) return;
          const row = button.closest('[data-complete-row]');
          const added = row.classList.contains('is-added');
          button.disabled = true;
          button.classList.add('paex-busy');

          const finish = (ok, remaining) => {
            button.disabled = false;
            button.classList.remove('paex-busy');
            if (!ok) {
              button.focus({ preventScroll: true }); // disabling blurred it
              this.announce(this.dataset.labelError || '');
              return;
            }
            const nowAdded = added ? remaining > 0 : true;
            row.classList.toggle('is-added', nowAdded);
            // a toggle keeps a STABLE name; aria-pressed carries the state
            button.setAttribute('aria-pressed', nowAdded ? 'true' : 'false');
            this.syncRowLocks();
            if (!added) {
              this.rowsHost.prepend(row); // added sorts to the top
              pushEvent(this.analytics, 'accessory_added', {
                parent_sku: this.dataset.parentSku || '',
                accessory_variant: button.dataset.variantId,
              });
            }
            this.applyCollapse();
            this.syncTotal();
            // prepending/disabling moved focus — put it back (WCAG 2.4.3)
            button.focus({ preventScroll: true });
          };

          if (added) {
            removeOneOf(button.dataset.variantId).then((result) => finish(result.ok, result.remaining));
          } else {
            addItems([{ id: Number(button.dataset.variantId), quantity: 1 }])
              .then((response) => finish(!!response, 1));
          }
        }

        // "1 item added · $36.98" — the only place the combined price shows
        syncTotal() {
          const total = this.querySelector('[data-complete-total]');
          if (!total) return;
          const added = Array.from(this.querySelectorAll('[data-complete-row].is-added [data-row-add]'));
          if (!added.length) {
            total.hidden = true;
            return;
          }
          const sum = added.reduce(
            (accumulator, button) => accumulator + (Number(button.dataset.price) || 0),
            Number(this.dataset.parentPrice) || 0
          );
          const count = this.querySelector('[data-total-count]');
          if (count) {
            const template = added.length === 1
              ? this.dataset.labelTotal
              : this.dataset.labelTotalPlural || this.dataset.labelTotal;
            count.textContent = (template || '').replace('{n}', String(added.length));
          }
          const price = this.querySelector('[data-total-price]');
          if (price) price.textContent = money(sum);
          total.hidden = false;
        }
      }
    );
  }
})();
